
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { hasLaunchDatePassed } from "@/utils/date-constants";
import { useAuth } from "@/contexts/auth";
import { Separator } from "@/components/ui/separator";

export default function MobileMenu() {
  const isLaunched = hasLaunchDatePassed();
  const homeUrl = isLaunched ? "/" : "/cayman";
  const homeNavState = isLaunched ? { noRedirect: true } : {};
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Function to handle hash navigation
  const handleHashNavigation = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    navigate('/', { state: { noRedirect: true } });
    
    // Close the sheet automatically
    const closeSheet = document.querySelector('[data-radix-collection-item]') as HTMLElement;
    if (closeSheet) closeSheet.click();
    
    // Give the page time to load before scrolling
    setTimeout(() => {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon">
          <Menu />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[300px] sm:w-[400px]">
        <nav className="flex flex-col gap-4 mt-8">
          {/* Main Navigation */}
          <SheetClose asChild>
            <Link
              to={homeUrl}
              state={homeNavState}
              className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
            >
              Home
            </Link>
          </SheetClose>
          {isLaunched && (
            <>
              <SheetClose asChild>
                <a
                  href="/#about"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                  onClick={(e) => handleHashNavigation(e, '#about')}
                >
                  About
                </a>
              </SheetClose>
              <SheetClose asChild>
                <a
                  href="/#values"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                  onClick={(e) => handleHashNavigation(e, '#values')}
                >
                  Values
                </a>
              </SheetClose>
              <SheetClose asChild>
                <a
                  href="/#how-it-works"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                  onClick={(e) => handleHashNavigation(e, '#how-it-works')}
                >
                  How It Works
                </a>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/polls"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                >
                  Polls
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/insights"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                >
                  Insights
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/civic-education"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                >
                  Civic Education
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/countries"
                  className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-nation-blue transition-colors"
                >
                  Countries
                </Link>
              </SheetClose>
            </>
          )}

          {/* Authentication Section */}
          {!user && (
            <>
              <Separator className="my-2" />
              <div className="space-y-3">
                <SheetClose asChild>
                  <Link
                    to="/auth"
                    className="flex items-center gap-2 text-lg font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Sign In
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/auth"
                    state={{ defaultTab: "signup" }}
                    className="flex items-center gap-2 text-lg font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                  >
                    Create Account
                  </Link>
                </SheetClose>
              </div>
            </>
          )}

          {/* Call to Action Section */}
          <Separator className="my-2" />
          {isLaunched ? (
            <SheetClose asChild>
              <a
                href="/#register"
                className="flex items-center gap-2 text-lg font-semibold text-nation-teal hover:text-nation-teal-dark transition-colors"
                onClick={(e) => handleHashNavigation(e, '#register')}
              >
                Become a Pollster
              </a>
            </SheetClose>
          ) : (
            <SheetClose asChild>
              <Link
                to="/cayman"
                className="flex items-center gap-2 text-lg font-semibold text-nation-teal hover:text-nation-teal-dark transition-colors"
              >
                Cayman Islands
              </Link>
            </SheetClose>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
